// Pure indexed matcher + row resolver for the automatic hard-name glossary.
// The engine hands us already-t2s rows. One resolution is shared by marker
// protection, TM signatures, telemetry and the glossary panel — no consumer
// is allowed to re-match independently.
import { MARKER_POOL, signatureFromPairs } from './glossary.js';
import { t2s, t2sBuffered } from './t2s.js';

export const AUTO_DICT_PROTOCOL = 'autodict-v1';

const textOrder = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const pointLength = (s) => Array.from(s).length;
const overlaps = (a, b) => a.start < b.end && a.end > b.start;
const termKey = (term) => `${term.source}\u0000${term.zh}`;
const EMPTY_SPANS = Object.freeze([]);
const EMPTY_RESOLUTION = Object.freeze({
  candidateSpans: EMPTY_SPANS,
  spans: EMPTY_SPANS,
  protectedSpans: EMPTY_SPANS,
  overflowSpans: EMPTY_SPANS,
  rejectedSpans: EMPTY_SPANS,
  marks: EMPTY_SPANS,
  signatureMaterial: Object.freeze({ pairs: EMPTY_SPANS, hasAuto: false }),
});

function cleanTerm(term, source, order) {
  const zh = t2s(String(term?.zh ?? '').trim()).text;
  const vi = String(term?.vi ?? '').trim().normalize('NFC');
  if (!zh || !vi) return null;
  return {
    ...term,
    zh,
    vi,
    source,
    order,
    pointLength: pointLength(zh),
  };
}

export function createTermIndex(terms, source = 'auto') {
  const normalized = [];
  const seen = new Map();
  for (const [order, raw] of (terms ?? []).entries()) {
    const term = cleanTerm(raw, raw?.source ?? source, order);
    if (!term) continue;
    const previous = seen.get(term.zh);
    if (previous) {
      if (previous.vi !== term.vi) {
        throw new Error(`conflicting canonical target for ${term.zh}`);
      }
      continue;
    }
    seen.set(term.zh, term);
    normalized.push(term);
  }
  const byFirst = new Map();
  for (const term of normalized) {
    const first = term.zh[0];
    const bucket = byFirst.get(first) ?? [];
    bucket.push(term);
    byFirst.set(first, bucket);
  }
  for (const bucket of byFirst.values()) {
    bucket.sort((a, b) => b.zh.length - a.zh.length || a.order - b.order);
  }
  return { terms: normalized, byFirst };
}

function applyRuntimePolicy(artifact, policy) {
  if (!policy) return { terms: artifact.terms, revision: artifact.revision, policy: null };
  if (policy.version !== 1 || policy.protocol !== 'autodict-runtime-policy-v1'
      || !/^sha256:[0-9a-f]{64}$/.test(policy.revision ?? '')
      || policy.baseArtifactRevision !== artifact.revision
      || !Array.isArray(policy.demote) || !Array.isArray(policy.guards)) {
    throw new Error('hard-name runtime policy is incompatible with the mined artifact');
  }
  const byZh = new Map(artifact.terms.map((term) => [t2s(term.zh).text, term]));
  const demoted = new Set();
  for (const row of policy.demote) {
    const zh = t2s(String(row?.zh ?? '').trim()).text;
    const term = byZh.get(zh);
    if (!zh || demoted.has(zh) || !term || term.vi !== row.vi) {
      throw new Error(`invalid hard-name runtime demotion: ${row?.id ?? ''}`);
    }
    demoted.add(zh);
  }
  const guards = new Map();
  for (const row of policy.guards) {
    const zh = t2s(String(row?.zh ?? '').trim()).text;
    const term = byZh.get(zh);
    const excludeWithin = [...new Set((row?.excludeWithin ?? [])
      .map((value) => t2s(String(value ?? '').trim()).text).filter(Boolean))];
    if (!zh || guards.has(zh) || demoted.has(zh) || !term || term.vi !== row.vi
        || !excludeWithin.length || excludeWithin.some((phrase) =>
          phrase === zh || !phrase.includes(zh))) {
      throw new Error(`invalid hard-name runtime guard: ${row?.id ?? ''}`);
    }
    guards.set(zh, excludeWithin);
  }
  return {
    terms: artifact.terms
      .filter((term) => !demoted.has(t2s(term.zh).text))
      .map((term) => {
        const excludeWithin = guards.get(t2s(term.zh).text);
        return excludeWithin ? { ...term, excludeWithin } : term;
      }),
    revision: policy.revision,
    policy,
  };
}

export function compileAutoArtifact(artifact, runtimePolicy = null) {
  if (artifact?.version !== 1 || !Array.isArray(artifact.terms)
      || typeof artifact.revision !== 'string' || !artifact.revision) {
    throw new Error('hard_names.json has an unsupported schema');
  }
  if ((artifact.protocol ?? AUTO_DICT_PROTOCOL) !== AUTO_DICT_PROTOCOL) {
    throw new Error(`hard_names.json needs protocol ${AUTO_DICT_PROTOCOL}`);
  }
  const effective = applyRuntimePolicy(artifact, runtimePolicy);
  const index = createTermIndex(effective.terms.map((term) => ({
    ...term,
    source: 'auto',
  })));
  index.terms.sort((a, b) => textOrder(a.zh, b.zh));
  return {
    version: 1,
    revision: effective.revision,
    minedRevision: artifact.revision,
    protocol: artifact.protocol ?? AUTO_DICT_PROTOCOL,
    sources: artifact.sources ?? [],
    runtimePolicy: effective.policy,
    terms: index.terms,
    index: createTermIndex(index.terms),
  };
}

function excludedByContext(row, span) {
  for (const phrase of span.term.excludeWithin ?? []) {
    let at = 0;
    while ((at = row.indexOf(phrase, at)) !== -1) {
      const end = at + phrase.length;
      if (at <= span.start && end >= span.end) return true;
      at = end;
    }
  }
  return false;
}

export function findCandidates(row, index) {
  if (!index?.terms?.length || !row) return [];
  const spans = [];
  for (let start = 0; start < row.length; start++) {
    const bucket = index.byFirst.get(row[start]);
    if (!bucket) continue;
    for (const term of bucket) {
      if (row.startsWith(term.zh, start)) {
        spans.push({ start, end: start + term.zh.length, term });
      }
    }
  }
  return spans;
}

function hasCandidate(row, index) {
  if (!index?.terms?.length || !row) return false;
  for (let start = 0; start < row.length; start++) {
    const bucket = index.byFirst.get(row[start]);
    if (bucket?.some((term) => row.startsWith(term.zh, start))) return true;
  }
  return false;
}

function claim(candidates, occupied = []) {
  const selected = [];
  const rejected = [];
  const ordered = [...candidates].sort((a, b) =>
    b.term.zh.length - a.term.zh.length
      || a.term.order - b.term.order
      || a.start - b.start);
  for (const span of ordered) {
    if ([...occupied, ...selected].some((other) => overlaps(span, other))) {
      rejected.push({ ...span, reason: 'overlap' });
    } else {
      selected.push(span);
    }
  }
  return { selected, rejected };
}

function countTerms(text, index) {
  const counts = new Map();
  for (const span of findCandidates(text, index)) {
    counts.set(span.term.zh, (counts.get(span.term.zh) ?? 0) + 1);
  }
  return counts;
}

export function createEntityContext({
  userCompiled = null,
  autoLexicon,
  documentText = '',
  suppressions = [],
} = {}) {
  const userTerms = (userCompiled?.terms ?? []).map((term, order) => ({
    ...term, source: 'user', order,
  }));
  const userIndex = createTermIndex(userTerms, 'user');
  const canonicalDocument = t2sBuffered(String(documentText ?? '')).text;
  const documentCounts = countTerms(canonicalDocument, autoLexicon?.index);
  const suppressed = new Set(
    [...suppressions].map((zh) => t2s(String(zh ?? '').trim()).text).filter(Boolean),
  );
  const userKeys = new Set(userIndex.terms.map((term) => term.zh));
  const documentTerms = (autoLexicon?.terms ?? [])
    .filter((term) => (documentCounts.get(term.zh) ?? 0) > 0)
    .map((term) => {
      const count = documentCounts.get(term.zh);
      let baseStatus = 'eligible';
      if (userKeys.has(term.zh)) baseStatus = 'overridden';
      else if (term.pointLength === 2 && count < 2) baseStatus = 'frequency';
      const status = suppressed.has(term.zh) ? 'suppressed' : baseStatus;
      return { zh: term.zh, vi: term.vi, src: term.src, count, status, baseStatus };
    });
  return {
    userIndex,
    autoIndex: autoLexicon?.index ?? createTermIndex([]),
    artifactRevision: autoLexicon?.revision ?? '',
    protocol: autoLexicon?.protocol ?? AUTO_DICT_PROTOCOL,
    documentCounts,
    documentTerms,
    suppressed,
  };
}

function firstTermOrder(spans, source) {
  const seen = new Set();
  const terms = [];
  for (const span of spans.filter((s) => s.term.source === source)
    .sort((a, b) => a.start - b.start)) {
    const key = termKey(span.term);
    if (!seen.has(key)) {
      seen.add(key);
      terms.push(span.term);
    }
  }
  return terms;
}

export function resolveRow(row, ctx) {
  // The dominant novel path is "no glossary term in this row". Avoid six
  // fresh arrays + signature objects per TM/protect pass; 60k unique rows can
  // otherwise create ~100 MiB of short-lived garbage before V8 collects it.
  if (!hasCandidate(row, ctx?.userIndex) && !hasCandidate(row, ctx?.autoIndex)) {
    return EMPTY_RESOLUTION;
  }
  const userCandidates = findCandidates(row, ctx?.userIndex);
  const user = claim(userCandidates);
  const candidateSpans = findCandidates(row, ctx?.autoIndex);
  const eligible = [];
  const rejectedSpans = [];
  for (const span of candidateSpans) {
    if (ctx.suppressed.has(span.term.zh)) {
      rejectedSpans.push({ ...span, reason: 'suppressed' });
    } else if (excludedByContext(row, span)) {
      rejectedSpans.push({ ...span, reason: 'context' });
    } else if (span.term.pointLength === 2
      && (ctx.documentCounts.get(span.term.zh) ?? 0) < 2) {
      rejectedSpans.push({ ...span, reason: 'frequency' });
    } else {
      eligible.push(span);
    }
  }
  const auto = claim(eligible, user.selected);
  rejectedSpans.push(...auto.rejected);
  const spans = [...user.selected, ...auto.selected].sort((a, b) => a.start - b.start);

  const pool = MARKER_POOL.filter((marker) => !row.includes(marker));
  const marksByTerm = new Map();
  const overflowKeys = new Set();
  const allocation = [
    ...firstTermOrder(spans, 'user'),
    ...firstTermOrder(spans, 'auto'),
  ];
  for (const term of allocation) {
    const key = termKey(term);
    if (pool.length) {
      marksByTerm.set(key, {
        marker: pool.shift(), vi: term.vi, count: 0,
      });
    } else {
      overflowKeys.add(key);
    }
  }

  const protectedSpans = [];
  const overflowSpans = [];
  for (const span of spans) {
    const mark = marksByTerm.get(termKey(span.term));
    if (!mark) {
      overflowSpans.push(span);
      continue;
    }
    mark.count++;
    protectedSpans.push({ ...span, marker: mark.marker });
  }
  const pairs = spans.map((span) => `${span.term.zh}\u001f${span.term.vi}`);
  return {
    candidateSpans,
    spans,
    protectedSpans,
    overflowSpans,
    rejectedSpans,
    marks: [...marksByTerm.values()],
    signatureMaterial: {
      pairs,
      hasAuto: spans.some((span) => span.term.source === 'auto'),
    },
  };
}

export function protectResolvedRow(row, resolution) {
  if (!resolution?.protectedSpans.length) return null;
  let text = row;
  const rightToLeft = [...resolution.protectedSpans].sort((a, b) => b.start - a.start);
  for (const span of rightToLeft) {
    text = text.slice(0, span.start) + span.marker + text.slice(span.end);
  }
  return { text, marks: resolution.marks };
}

export function signatureForResolution(resolution, {
  artifactRevision = '', protocol = AUTO_DICT_PROTOCOL,
} = {}) {
  const material = resolution?.signatureMaterial;
  if (!material?.pairs.length) return null;
  const extra = material.hasAuto
    ? `auto=${artifactRevision}\u001fprotocol=${protocol}`
    : '';
  return signatureFromPairs(material.pairs, extra);
}
